import { useEffect, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { useQueryClient } from "@tanstack/react-query";

import Loader from "core/components/Loader";
import { requiresReauthentication } from "./authError";
import { endSession } from "./endSession";
import {
  isRecentRedirect,
  readLastRedirectAt,
  recordRedirect,
} from "./redirectLoop";

const HandleAuthError = () => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { error } = auth;

  // An expired session isn't a failure — it's the normal end of a login, and
  // the app can't renew its way out of it. Send the user back to Keycloak
  // rather than to "Something went wrong", keeping wherever they were so they
  // return to it. With the SSO session still alive this is invisible: Keycloak
  // hands them straight back, logged in.
  // Read once per mount, before this component writes its own timestamp: a
  // recent one then means a previous page load redirected and the reload came
  // back to the same failure.
  const previousRedirectAt = useRef(readLastRedirectAt()).current;
  const shouldRedirect =
    requiresReauthentication(error) && !isRecentRedirect(previousRedirectAt);

  const hasRedirected = useRef(false);

  useEffect(() => {
    if (!shouldRedirect || hasRedirected.current) return undefined;

    hasRedirected.current = true;

    const redirectToLogin = () => {
      recordRedirect();
      return endSession({ auth, queryClient });
    };

    // Navigating while the browser knows it's offline just swaps the app for
    // the browser's own error page, so wait for the connection to come back.
    // Waking a laptop is exactly this: the renewal fails before the network
    // is up.
    if (navigator.onLine === false) {
      window.addEventListener("online", redirectToLogin, { once: true });
      return () => window.removeEventListener("online", redirectToLogin);
    }

    void redirectToLogin();
    return undefined;
  }, [shouldRedirect, auth, queryClient]);

  // Every other guard renders null while an error is set, so without this the
  // user watches a blank page through the redirect.
  if (shouldRedirect) {
    return <Loader style={{ height: "100dvh", paddingBottom: "20vh" }} />;
  }

  if (error) {
    throw error;
  }

  return null;
};

export default HandleAuthError;
